import type { ComponentType } from 'react';
import {
  HomeIcon,
  MenuIcon,
  SearchIcon,
  BasketIcon,
  AccountIcon,
  LanguageIcon,
} from '../../icons/NavIcons';
import type { IconProps } from '../../icons/icons.types';
import { Badge, Bar, IconContainer, IconWrapper, TabItem, TabLabel } from './MobileTabBar.styles';
import type { MobileTabBarProps, MobileTabIcon, MobileTabItem } from './MobileTabBar.types';

// Annotated, not `as const`, so a glyph added to MobileTabIcon without a map entry fails the build.
const mobileIconMap: Record<MobileTabIcon, ComponentType<IconProps>> = {
  home: HomeIcon,
  menu: MenuIcon,
  search: SearchIcon,
  basket: BasketIcon,
  account: AccountIcon,
  language: LanguageIcon,
};

function TabContent({ item }: { item: MobileTabItem }) {
  const IconComponent = mobileIconMap[item.icon];
  const badge = item.badge != null && item.badge > 0 ? item.badge : undefined;

  return (
    <>
      <IconContainer>
        <IconWrapper>
          {item.icon === 'basket' ? (
            <BasketIcon size={22} count={badge} />
          ) : (
            <>
              <IconComponent size={22} />
              {badge !== undefined && <Badge>{badge}</Badge>}
            </>
          )}
        </IconWrapper>
      </IconContainer>
      <TabLabel>{item.label}</TabLabel>
    </>
  );
}

/** `aria-pressed` is invalid on an anchor, so an `href` item marks itself with `aria-current`. */
export function MobileTabBar({
  items,
  navigationLabel = 'Main navigation',
  linkComponent = 'a',
}: MobileTabBarProps) {
  return (
    <Bar aria-label={navigationLabel}>
      {items.map((item) => {
        const isLink = Boolean(item.href);

        return (
          <TabItem
            key={item.id}
            as={isLink ? linkComponent : 'button'}
            type={isLink ? undefined : 'button'}
            href={item.href}
            onClick={item.onClick}
            $active={item.active}
            aria-current={isLink && item.active ? 'page' : undefined}
            aria-label={item.label}
          >
            <TabContent item={item} />
          </TabItem>
        );
      })}
    </Bar>
  );
}
